import React, { useContext, useEffect, useState } from 'react';
import { IProps, IState } from './types';
import * as S from './styles';
import { Context } from "routes/private/Editor/Context";
import { IElement, IArtboardAttributes, IGroupAttributes } from '@routes/private/Editor/types';
import { findElementById, findArtboard, findArtboardIndex, getBoundaries, getRelativeClientRect, ILimits } from '@utils/editor';
import { getArtboardZIndexChain } from '@utils/zIndexChains';
import { IResizeValues } from '@routes/private/Editor/Editor/CommonCommandsOptions/ResizeCommandOption/types';
import ClickOutside from '@components/ClickOutside';
import MoveForwardIcon from '@assets/icons/moveForwardIcon';
import MoveBackwardIcon from '@assets/icons/moveBackwardIcon';
import MoveFrontIcon from '@assets/icons/moveFrontIcon';
import MoveBackIcon from '@assets/icons/moveBackIcon';
import MoveLeftIcon from '@assets/icons/moveLeftIcon';
import MoveTopIcon from '@assets/icons/moveTopIcon';
import MoveCenterIcon from '@assets/icons/moveCenterIcon';
import MoveMiddleIcon from '@assets/icons/moveMiddleIcon';
import MoveBottomIcon from '@assets/icons/moveBottomIcon';
import MoveRightIcon from '@assets/icons/moveRightIcon';
import { cloneDeep } from 'lodash';
import { IAttributes } from '@routes/private/Editor/Editor/Canvas/AddIns/GuidesAddIn/types';


const PositionCommandOption : React.FC<IProps> = () => {
  const { editorStatus, data, artboardsZIndexChain, onCommandChange, stageRef } = useContext(Context);
  const [open, setOpen] = useState(false); 
  const [chains, setChains] = useState<IState['chains']>([]);
  const [boundaries, setBoundaries] = useState<IState['boundaries']>([]); 
  const [enableChange, setEnableChange] = useState([true, true, true, true]); 
  const [enableAlignment, setEnableAlignment] = useState([true, true, true, true, true, true]); 

  const handleOpen = () => {
    setOpen(!open);
    if (!open) {
      handleEnablePositions();
      handleBoundaries();
    }
  }

  useEffect(() => {
    if (data[0]?.children) {
      let newChains: IState['chains'] = [];
      if (data[0].type === "Artboards") {
        data[0].children.map((child, index) => {
          if (artboardsZIndexChain && artboardsZIndexChain[index] && artboardsZIndexChain[index].zIndexChain && artboardsZIndexChain[index].zIndexChain.length > 0) newChains = [...newChains, {artboardIndex: index, zIndexChain: artboardsZIndexChain[index].zIndexChain}]
          else if (child.children) newChains = [...newChains, {artboardIndex: index, zIndexChain: getArtboardZIndexChain(child)}];
        })
      } else { 
        if (artboardsZIndexChain && artboardsZIndexChain[0] && artboardsZIndexChain[0].zIndexChain.length > 0) newChains = [...newChains, {artboardIndex: 0, zIndexChain: artboardsZIndexChain[0].zIndexChain}]
        else if (data[0].children) newChains = [...newChains, {artboardIndex: 0, zIndexChain: getArtboardZIndexChain(data[0])}];
      }
      setChains(newChains);
    }
  }, [data]);

  useEffect(() => {
    if (open) {
      handleEnablePositions();
      handleBoundaries();
    }
  }, [JSON.stringify(editorStatus.selectedElementsId)]);

  useEffect(() => {
    handleEnablePositions();
  }, [chains]);

  useEffect(() => {
    handleEnableBoundaries();
  }, [boundaries]);

  const handleEnablePositions = () => {
    if (chains.length && editorStatus.selectedElementsId.length) {
      let newEnableChange = [true, true, true, true];
      editorStatus.selectedElementsId.map(elementId => {
        chains.map(chain => {
          const elementIndex = chain.zIndexChain.findIndex(id => (id === elementId));
          if (elementIndex >= 0) {
            if (elementIndex + 1 === chain.zIndexChain.length) {
              newEnableChange[0] = false;
              newEnableChange[2] = false;
            } else if (elementIndex === 0) {
              newEnableChange[1] = false;
              newEnableChange[3] = false;
            }
          }
        })
      });
      setEnableChange(newEnableChange);
    }
  }

  const returnChain = (parentChains: IState['chains'], artboardIndex: number) => {
    let zIndexChain: string[] = [];
    const parentIndex = parentChains.findIndex(chain => (chain.artboardIndex === artboardIndex));
    if (parentIndex >= 0) zIndexChain = parentChains[parentIndex].zIndexChain;
    return zIndexChain;
  }

  const updateChain = (newChains: IState['chains']) => {
    setChains(newChains);
    onCommandChange('changePositionsChain', newChains);
  }

  const changePosition = (value: string, canChange: boolean) => {
    if (canChange) {  
      let newChains: IState['chains'] = cloneDeep(artboardsZIndexChain);
      editorStatus.selectedElementsId.map((id: string, index: number) => {
        if (data[0]?.children) {
          let artboardIndex = findArtboardIndex(data[0].children, id); 
          if (!artboardIndex) artboardIndex = 0;
          const index = newChains.findIndex(chain => (chain.artboardIndex === artboardIndex));
          if (index === -1) newChains = [...newChains, {artboardIndex: artboardIndex, zIndexChain: returnChain(chains, artboardIndex)}];
          newChains = changeObjectPosition(newChains, artboardIndex, value, id);
        }
      })
      updateChain(newChains);
    }
  }

  const moveFromTo = (array: string[], from: number, to: number) => {
    const newArray = [...array];
    const element = newArray[from];
    newArray.splice(from, 1);
    newArray.splice(to, 0, element);
    return newArray;
  };

  const changeObjectPosition = (parentChains: IState['chains'], artboardIndex: number, value: string, id: string) => {
    let zIndexChain: string[] = parentChains[artboardIndex] ? parentChains[artboardIndex].zIndexChain : returnChain(parentChains, artboardIndex);
    const elementIndex = zIndexChain.findIndex(zIndex => (zIndex === id));
    if (elementIndex >= 0) {
      if (value === 'forward') {
        if (elementIndex + 1 < zIndexChain.length) {
          zIndexChain = moveFromTo(zIndexChain, elementIndex, elementIndex + 1);
        }
      } else if (value === 'backward') {
        if (elementIndex > 0) {
          zIndexChain = moveFromTo(zIndexChain, elementIndex, elementIndex - 1);
        }
      } else if (value === 'front') {
        zIndexChain = moveFromTo(zIndexChain, elementIndex, zIndexChain.length - 1);
      } else if (value === 'back') {
        zIndexChain = moveFromTo(zIndexChain, elementIndex, 0);
      }
    }
    parentChains[artboardIndex] = {artboardIndex: artboardIndex, zIndexChain: zIndexChain};
    return parentChains;
  }

  const handleBoundaries = () => {
    let newBoundaries: IState['boundaries'] = [];
    editorStatus.selectedElementsId.map((id: string, index: number) => {
      const element = findElementById(data, id);
      if (data[0]?.children && element) {
        let artboard = findArtboard(data[0].children, id); 
        if (!artboard) artboard = data[0];
        const parentIndex = newBoundaries.findIndex(boundary => (boundary.artboard.id === artboard?.id));
        if (parentIndex >= 0) newBoundaries[parentIndex] = {...newBoundaries[parentIndex], children: [...newBoundaries[parentIndex].children, element]}
        else newBoundaries = [...newBoundaries, {artboard, children: [element]}];
      }
    })
    newBoundaries.map((boundary, index: number) => {
      if (boundary.artboard) newBoundaries[index] = {...boundary, limits: getBoundaries(stageRef.current, boundary.artboard, boundary.children)}
    });
    setBoundaries([...newBoundaries]);
  }

  const getNewPoint = (limits: ILimits, item: IAttributes, element: IElement, value:string)=>
  {
    let point = cloneDeep(item);
    if (value==="left")
    {
      point.x = element.attributes.x - (point.x - limits.left);
      point.y = element.attributes.y
    }
    if (value==="right")
    {
      point.x = element.attributes.x + limits.right - (point.x + point.width);
      point.y = element.attributes.y
    }
    if (value==="top")
    {
      point.y = element.attributes.y - (point.y - limits.top);
      point.x = element.attributes.x
    }
    if (value==="bottom")
    {
      point.y = element.attributes.y + limits.bottom - (point.y + point.height);
      point.x = element.attributes.x
    }
    if (value === "center")
    {
      point.x = element.attributes.x - point.x + (limits.right + limits.left - point.width)/2;
      point.y = element.attributes.y
    }
    if (value === "middle")
    {
      point.y = element.attributes.y - point.y + (limits.bottom + limits.top - point.height)/2;
      point.x = element.attributes.x
    }
    return point;      
  }

  const changeAlignment = (value: string, canChange: boolean) => {
    if (canChange) {
      let updateResizes: {id: string, resizeValues: IResizeValues}[] = [];
      boundaries.map(boundary => {
        boundary.children.map(element => {
          const item = getRelativeClientRect(stageRef.current, `${boundary.artboard.id}-object`, element.id, element.type === "Image" ? true : false);
          if (element && item && boundary.limits) {

            let point = getNewPoint(boundary.limits, item, element, value); 
            item.node?.x(point.x);
            item.node?.y(point.y);

            const deltaX = point.x - element.attributes.x, deltaY = point.y - element.attributes.y;
            let updateResize: {id: string, resizeValues: IResizeValues} = {
              id: element.id,
              resizeValues: {
                x: point.x,
                y: point.y,
                width: element.attributes.width,
                height: element.attributes.height,
                image: element.type === "Image" ? {
                  ...element.attributes.image,
                  x: element.attributes.image.x + deltaX,
                  y: element.attributes.image.y + deltaY,
                } : undefined,
                rotation: element.attributes.rotation ?? undefined
              }
            };

            updateResizes = [...updateResizes, updateResize];
          }
        })
      })
      updateBoundaries(updateResizes);
    }
  }

  const updateBoundaries = (elements: {id: string, resizeValues: IResizeValues}[]) => {
    let ids: string[] = [];
    let resizes: IResizeValues[] = [];
    elements.map(element => {
      ids = [...ids, element.id];
      let resizeValue: IResizeValues = {
        x: element.resizeValues.x,
        y: element.resizeValues.y,
        width: element.resizeValues.width,
        height: element.resizeValues.height,       
        image: element.resizeValues.image,
        rotation: element.resizeValues.rotation ?? undefined
      }
      resizes = [...resizes, resizeValue];
    })
    onCommandChange('resize', undefined, ids, resizes);
    handleBoundaries();
  }

  const handleEnableBoundaries = () => {
    let newEnableAlignment = [false, false, false, false, false, false];
    boundaries.map(boundary => {
      boundary.children.map(element => {
        const item = getRelativeClientRect(stageRef.current, `${boundary.artboard.id}-object`, element.id);
        ["left", "top", "center", "middle", "bottom", "right"].map(value => {
          if (element && item && boundary.limits) {
            let point = getNewPoint(boundary.limits, item, element, value);
            if (point.x < element.attributes.x && value === "left")
              newEnableAlignment[0] = true;
            if (point.y < element.attributes.y && value === "top")
              newEnableAlignment[1] = true;
            if (point.x.toFixed(2) !== element.attributes.x.toFixed(2) && value === "center")
              newEnableAlignment[2] = true;
            if (point.y.toFixed(2) !== element.attributes.y.toFixed(2) && value === "middle")
              newEnableAlignment[3] = true;
            if (element.attributes.y < point.y && value === "bottom")
              newEnableAlignment[4] = true;
            if (element.attributes.x < point.x && value === "right")
              newEnableAlignment[5] = true;
          }
        });
      });
    });
    setEnableAlignment(newEnableAlignment);
  }

  return (
    <S.Root>
      <ClickOutside onClickOutside={()=> setOpen(false)}>
      <S.ButtonContainer onClick={handleOpen} isSelected={open}>
        Position
      </S.ButtonContainer>
      { open &&
        <S.OptionsContainer>
          <S.OptionsRowContainer>
            <S.IconContainer
              backgroundColorValue={enableChange[0] ? 'rgba(18, 36, 66, 0.1)' : 'auto'}
              onClick={() => changePosition('forward', enableChange[0])}
            >
              <MoveForwardIcon fillColor={enableChange[0] ? undefined : '#A0A5AB'} />
              <S.IconText>
                Move Forward
              </S.IconText>
            </S.IconContainer>
            <S.IconContainer
              backgroundColorValue={enableChange[1] ? 'rgba(18, 36, 66, 0.1)' : 'auto'}
              onClick={() => changePosition('backward', enableChange[1])}
            >
              <MoveBackwardIcon fillColor={enableChange[1] ? undefined : '#A0A5AB'} />
              <S.IconText>
                Send Backward
              </S.IconText>
            </S.IconContainer>
            <S.IconContainer
              backgroundColorValue={enableChange[2] ? 'rgba(18, 36, 66, 0.1)' : 'auto'}
              onClick={() => changePosition('front', enableChange[2])}
            >
              <MoveFrontIcon fillColor={enableChange[2] ? undefined : '#A0A5AB'} />
              <S.IconText>
                Send to Front
              </S.IconText>
            </S.IconContainer>
            <S.IconContainer
              backgroundColorValue={enableChange[3] ? 'rgba(18, 36, 66, 0.1)' : 'auto'}
              onClick={() => changePosition('back', enableChange[3])}
            >
              <MoveBackIcon fillColor={enableChange[3] ? undefined : '#A0A5AB'} />
              <S.IconText>
                Send to Back
              </S.IconText>
            </S.IconContainer>
          </S.OptionsRowContainer>
          <S.LineDivider />
          <S.OptionsRowContainer>
            <S.IconContainer
              backgroundColorValue={enableAlignment[0] ? 'rgba(18, 36, 66, 0.1)' : 'auto'}
              onClick={() => changeAlignment('left', enableAlignment[0])}
            >
              <MoveLeftIcon fillColor={enableAlignment[0] ? undefined : '#A0A5AB'} />
              <S.IconText>
                Left
              </S.IconText>
            </S.IconContainer>
            <S.IconContainer
              backgroundColorValue={enableAlignment[1] ? 'rgba(18, 36, 66, 0.1)' : 'auto'}
              onClick={() => changeAlignment('top', enableAlignment[1])}
            >
              <MoveTopIcon fillColor={enableAlignment[1] ? undefined : '#A0A5AB'} />
              <S.IconText>
                Top
              </S.IconText>
            </S.IconContainer>
            <S.IconContainer
              backgroundColorValue={enableAlignment[2] ? 'rgba(18, 36, 66, 0.1)' : 'auto'}
              onClick={() => changeAlignment('center', enableAlignment[2])}
            >
              <MoveCenterIcon fillColor={enableAlignment[2] ? undefined : '#A0A5AB'} />
              <S.IconText>
                Center
              </S.IconText>
            </S.IconContainer>
            <S.IconContainer
              backgroundColorValue={enableAlignment[3] ? 'rgba(18, 36, 66, 0.1)' : 'auto'}
              onClick={() => changeAlignment('middle', enableAlignment[3])}
            >
              <MoveMiddleIcon fillColor={enableAlignment[3] ? undefined : '#A0A5AB'} />
              <S.IconText>
                Middle
              </S.IconText>
            </S.IconContainer>
            <S.IconContainer
              backgroundColorValue={enableAlignment[4] ? 'rgba(18, 36, 66, 0.1)' : 'auto'}
              onClick={() => changeAlignment('bottom', enableAlignment[4])}
            >
              <MoveBottomIcon fillColor={enableAlignment[4] ? undefined : '#A0A5AB'} />
              <S.IconText>
                Bottom
              </S.IconText>
            </S.IconContainer>
            <S.IconContainer
              backgroundColorValue={enableAlignment[5] ? 'rgba(18, 36, 66, 0.1)' : 'auto'}
              onClick={() => changeAlignment('right', enableAlignment[5])}
            >
              <MoveRightIcon fillColor={enableAlignment[5] ? undefined : '#A0A5AB'} />
              <S.IconText>
                Right
              </S.IconText>
            </S.IconContainer>
          </S.OptionsRowContainer>
        </S.OptionsContainer>
      }
      </ClickOutside>
    </S.Root>
  );
}

export default PositionCommandOption;